import { Ionicons } from "@expo/vector-icons";
import { type IThemePalette } from "../types";
import { BrandColors } from "../base";

/**
 * Domain entity types supported by ThemedChip.
 */
export type ChipEntityType =
  | "contact"
  | "group"
  | "memory"
  | "media"
  | "tag"
  | "share"
  | "user"
  | "message"
  | "place"
  | "anchor"
  | "archival"
  | "general";

/**
 * Visual color and icon configuration for an entity chip.
 */
export interface IChipVisualConfig {
  defaultIcon: keyof typeof Ionicons.glyphMap;
  bgColor: string;
  borderColor: string;
  textColor: string;
  prefix?: string;
  badgeText?: string;
}

/**
 * Interactive filter chip tokens (used in TagSelector and search filter toolbars).
 */
export interface IFilterChipTokens {
  restingBg: string;
  activeBg: string;
  solidActiveBg: string;
  restingBorder: string;
  activeBorder: string;
  restingText: string;
  activeText: string;
  solidActiveText: string;
  borderRadius: number;
  paddingHorizontal: number;
  paddingVertical: number;
  fontSize: number;
}

/**
 * Sizing tokens for standard vs compact chip density.
 */
export interface IChipSizeTokens {
  minHeight: number;
  fontSize: number;
  iconSize: number;
  thumbSize: number;
  borderRadius: number;
}

/**
 * Complete chip component theme recipe.
 */
export interface IChipTheme {
  entities: Record<ChipEntityType, IChipVisualConfig>;
  selfHighlight: IChipVisualConfig;
  filterChip: IFilterChipTokens;
  sizes: {
    standard: IChipSizeTokens;
    compact: IChipSizeTokens;
  };
}

/**
 * Returns color and icon configuration for each entity chip type.
 * Aligns with ThemedEditor designer chip tokens and strictly adheres to the Purple Ban:
 * - Contact / User: Emerald (#047857 / #34D399)
 * - Group / Message: Royal Blue (#1D4ED8 / #60A5FA)
 * - Media: Amber Gold (#B45309 / #FBBF24)
 * - Memory: Rose Crimson (#BE123C / #FB7185)
 * - Tag: Tangerine (#9C4800 / #F9A347)
 * - Share: Botanical Mauve Rose (#7D2D68 / #DFA8CE)
 * - Archival: Butter Bean (#1C1917 / ButterBean)
 * - General: Slate (#334155 / #E2E8F0)
 */
export function getChipVisualConfig(entityType: string, isDark: boolean): IChipVisualConfig {
  const norm = (entityType || "general").toLowerCase();

  switch (norm) {
    case "contact":
      return {
        defaultIcon: "person-circle-outline",
        prefix: "@",
        badgeText: "Contact",
        bgColor: isDark ? "rgba(52, 211, 153, 0.12)" : "rgba(5, 150, 105, 0.08)",
        borderColor: isDark ? "rgba(52, 211, 153, 0.4)" : "rgba(5, 150, 105, 0.3)",
        textColor: isDark ? "#34D399" : "#047857",
      };
    case "group":
      return {
        defaultIcon: "people-outline",
        prefix: "@",
        badgeText: "Group",
        bgColor: isDark ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)",
        borderColor: isDark ? "rgba(96, 165, 250, 0.4)" : "rgba(37, 99, 235, 0.3)",
        textColor: isDark ? "#60A5FA" : "#1D4ED8",
      };
    case "memory":
      return {
        defaultIcon: "sparkles-outline",
        prefix: "#",
        badgeText: "Memory",
        bgColor: isDark ? "rgba(244, 63, 94, 0.12)" : "rgba(225, 29, 72, 0.08)",
        borderColor: isDark ? "rgba(244, 63, 94, 0.4)" : "rgba(225, 29, 72, 0.3)",
        textColor: isDark ? "#FB7185" : "#BE123C",
      };
    case "media":
      return {
        defaultIcon: "images-outline",
        prefix: "+",
        badgeText: "Media",
        bgColor: isDark ? "rgba(251, 191, 36, 0.12)" : "rgba(217, 119, 6, 0.08)",
        borderColor: isDark ? "rgba(251, 191, 36, 0.4)" : "rgba(217, 119, 6, 0.3)",
        textColor: isDark ? "#FBBF24" : "#B45309",
      };
    case "tag":
      return {
        defaultIcon: "pricetag-outline",
        prefix: "#",
        badgeText: "Tag",
        bgColor: isDark ? "rgba(242, 133, 0, 0.16)" : "#FFF1E6",
        borderColor: isDark ? "rgba(242, 133, 0, 0.35)" : "#FFC499",
        textColor: isDark ? "#F9A347" : "#9C4800",
      };
    case "share":
      return {
        defaultIcon: "share-social-outline",
        prefix: "",
        badgeText: "Share",
        bgColor: isDark ? "rgba(200, 133, 180, 0.18)" : "#FBF1F7",
        borderColor: isDark ? "rgba(200, 133, 180, 0.38)" : "#E5B8D8",
        textColor: isDark ? "#DFA8CE" : "#7D2D68",
      };
    case "user":
      return {
        defaultIcon: "person-outline",
        prefix: "@",
        badgeText: "User",
        bgColor: isDark ? "rgba(52, 211, 153, 0.12)" : "rgba(5, 150, 105, 0.08)",
        borderColor: isDark ? "rgba(52, 211, 153, 0.4)" : "rgba(5, 150, 105, 0.3)",
        textColor: isDark ? "#34D399" : "#047857",
      };
    case "message":
      return {
        defaultIcon: "mail-outline",
        prefix: "@",
        badgeText: "Message",
        bgColor: isDark ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)",
        borderColor: isDark ? "rgba(96, 165, 250, 0.4)" : "rgba(37, 99, 235, 0.3)",
        textColor: isDark ? "#60A5FA" : "#1D4ED8",
      };
    case "place":
      return {
        defaultIcon: "location-outline",
        prefix: "",
        badgeText: "Place",
        bgColor: isDark ? "rgba(168, 85, 247, 0.16)" : "rgba(168, 85, 247, 0.12)",
        borderColor: isDark ? "rgba(168, 85, 247, 0.4)" : "rgba(147, 51, 234, 0.35)",
        textColor: isDark ? "#C084FC" : "#7E22CE",
      };
    case "anchor":
      return {
        defaultIcon: "boat-outline",
        prefix: "",
        badgeText: "Anchor",
        bgColor: isDark ? "rgba(202, 210, 60, 0.18)" : "#EBF5D5",
        borderColor: isDark ? "rgba(202, 210, 60, 0.45)" : "#CAD23C",
        textColor: isDark ? "#CAD23C" : "#1D1B20",
      };
    case "archival":
      return {
        defaultIcon: "bookmark-outline",
        prefix: "",
        badgeText: "Archival",
        bgColor: isDark ? "rgba(253, 250, 216, 0.15)" : BrandColors.butterBean.hex,
        borderColor: isDark ? "rgba(253, 250, 216, 0.25)" : "#E8E2BC",
        textColor: isDark ? BrandColors.butterBean.hex : "#1C1917",
      };
    case "general":
    default:
      return {
        defaultIcon: "pricetag-outline",
        prefix: "",
        badgeText: "Item",
        bgColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#F1F5F9",
        borderColor: isDark ? "rgba(255, 255, 255, 0.16)" : "#CBD5E1",
        textColor: isDark ? "#E2E8F0" : "#334155",
      };
  }
}

/**
 * Returns tag type icon when entityType is "tag".
 */
export function getTagTypeIcon(tagType?: string): keyof typeof Ionicons.glyphMap {
  switch ((tagType || "").toLowerCase()) {
    case "contact":
    case "user":
      return "person-circle-outline";
    case "group":
      return "people-outline";
    case "memory":
      return "sparkles-outline";
    case "media":
      return "images-outline";
    case "place":
      return "location-outline";
    case "anchor":
      return "boat-outline";
    case "system":
      return "hardware-chip-outline";
    default:
      return "pricetag-outline";
  }
}

/**
 * Pure factory generating the chip component theme.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved IChipTheme.
 */
export function getChipTheme(palette: IThemePalette, isDark: boolean): IChipTheme {
  const entityTypes: ChipEntityType[] = [
    "contact",
    "group",
    "memory",
    "media",
    "tag",
    "share",
    "user",
    "message",
    "place",
    "anchor",
    "archival",
    "general",
  ];

  const entities = entityTypes.reduce(
    (acc, ent) => {
      acc[ent] = getChipVisualConfig(ent, isDark);
      return acc;
    },
    {} as Record<ChipEntityType, IChipVisualConfig>
  );

  return {
    entities,
    selfHighlight: {
      defaultIcon: "person",
      bgColor: isDark ? "rgba(202, 138, 4, 0.2)" : "rgba(202, 138, 4, 0.12)",
      borderColor: isDark ? "#CA8A04" : "#EAB308",
      textColor: isDark ? "#FACC15" : "#854D0E",
    },
    filterChip: {
      restingBg: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
      activeBg: palette.buttonSecondary,
      solidActiveBg: palette.tint,
      restingBorder: palette.border,
      activeBorder: palette.tint,
      restingText: palette.text,
      activeText: palette.tint,
      solidActiveText: "#FFFFFF",
      borderRadius: 12,
      paddingHorizontal: 8,
      paddingVertical: 3,
      fontSize: 11,
    },
    sizes: {
      standard: {
        minHeight: 19,
        fontSize: 11,
        iconSize: 11,
        thumbSize: 14,
        borderRadius: 4,
      },
      compact: {
        minHeight: 17,
        fontSize: 10,
        iconSize: 10,
        thumbSize: 12,
        borderRadius: 3,
      },
    },
  };
}
